
import React, { useState, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Phone, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdminAuth } from '@/hooks/useAdminAuth';

interface TechnicianProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  address?: string;
  profileimage?: string;
  isAdmin: boolean;
  isTechnician: boolean;
  assignedZones?: string[];
}

const TechnicianList: React.FC = () => {
  const [technicians, setTechnicians] = useState<TechnicianProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { logAdminActivity } = useAdminAuth();
  
  useEffect(() => {
    const fetchTechnicians = async () => {
      try {
        setIsLoading(true);
        
        // Fetch all users from user_profiles
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*');
          
        if (error) {
          throw error;
        }
        
        if (data) {
          // Check admin status for each user
          const techData: TechnicianProfile[] = await Promise.all(
            data.map(async (user) => {
              // Check if user is admin
              const { data: isAdminResult } = await supabase.rpc('is_admin', { 
                user_uuid: user.id 
              });

              return {
                id: user.id,
                name: user.name || 'Unnamed User',
                email: user.email,
                phone: user.phone || 'No phone provided',
                address: user.address,
                profileimage: user.profileimage,
                isAdmin: isAdminResult || false,
                // For now, consider all users as potential technicians
                // In a real implementation, you'd have a separate technician role system
                isTechnician: user.email?.includes('tech') || user.name?.toLowerCase().includes('tech') || false,
                assignedZones: ['Palm Springs'] // Mock data
              };
            })
          );
          
          // Filter to only show users marked as technicians or admins
          const filteredTechs = techData.filter(tech => tech.isTechnician || tech.isAdmin);
          setTechnicians(filteredTechs);
          
          logAdminActivity('technician_list_viewed', { count: filteredTechs.length });
        }
      } catch (error) {
        console.error('Error fetching technicians:', error);
        toast.error('Failed to load technician data');
        logAdminActivity('technician_list_error', { error: error.message });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTechnicians();
  }, [logAdminActivity]);
  
  const toggleTechnicianStatus = async (techId: string, currentStatus: boolean) => {
    try {
      // Note: This would need to be updated when proper role columns are added to the database
      toast.success("Feature not fully implemented", {
        description: "Role management requires database schema updates."
      });
      
      // Mock update for now
      setTechnicians(prev => prev.map(tech => 
        tech.id === techId 
          ? { ...tech, isTechnician: !tech.isTechnician }
          : tech
      ));

      logAdminActivity('technician_status_changed', { 
        technicianId: techId, 
        newStatus: !currentStatus 
      });
    } catch (error) {
      console.error('Error updating technician status:', error);
      toast.error('Failed to update technician status');
    }
  };
  
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }
  
  return (
    <div className="rounded-md border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead className="hidden sm:table-cell">Email</TableHead>
            <TableHead className="hidden md:table-cell">Phone</TableHead>
            <TableHead className="hidden lg:table-cell">Zones</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {technicians.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                No technicians found
              </TableCell>
            </TableRow>
          ) : (
            technicians.map((tech) => (
              <TableRow key={tech.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {tech.name}
                    {tech.isAdmin && (
                      <Badge variant="outline" className="ml-1">
                        <Shield size={12} className="mr-1" />
                        Admin
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="hidden sm:table-cell">
                  {tech.email}
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  <a href={`tel:${tech.phone}`} className="flex items-center gap-1 text-primary">
                    <Phone size={14} />
                    {tech.phone}
                  </a>
                </TableCell>
                <TableCell className="hidden lg:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {tech.assignedZones?.map((zone: string) => (
                      <Badge key={zone} variant="secondary" className="text-xs">
                        {zone}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={tech.isTechnician ? "default" : "outline"}>
                    {tech.isTechnician ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        toast("Edit technician", {
                          description: `Editing ${tech.name}'s details.`
                        });
                        logAdminActivity('technician_edit_initiated', { technicianId: tech.id });
                      }}
                    >
                      Edit
                    </Button>
                    
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant={tech.isTechnician ? "destructive" : "default"}>
                          {tech.isTechnician ? 'Deactivate' : 'Activate'}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            {tech.isTechnician 
                              ? `Deactivate ${tech.name}?` 
                              : `Activate ${tech.name}?`}
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {tech.isTechnician 
                              ? "This technician will no longer be able to access the system or be assigned to visits." 
                              : "This will restore the technician's access to the system."}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => toggleTechnicianStatus(tech.id, tech.isTechnician)}>
                            {tech.isTechnician ? 'Deactivate' : 'Activate'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default TechnicianList;
